import { enumObjClassNames, kDefaultPlanFields } from "@coreapp/constants";
import { DEFAULT_POSITION_PLAN_FIELDS } from "@coreapp/CoreApp/position-management.constants";
import {
  GetFieldLabelFromName,
  GetFieldNameLocalizationMap,
  GetFieldNamesFromId,
  GetPlanAdditionalFieldOptions,
  GetPlanFieldsLocalizationMap
} from "@/Utils/DataLib";
import type { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { filterInternalFields as filterInternalFieldsUtil } from "@/Utils/FilterInternalFields";

const filterInternalFields = (fields: string[]) =>
  (filterInternalFieldsUtil(fields) as string[]).filter(field => field !== "objType");

export function usePlanFields({
  isPosChart,
  mockDoc,
  searchQuery
}: {
  isPosChart: boolean;
  mockDoc: Nullable<QXDoc>;
  searchQuery: string;
}) {
  // Required field IDs
  const requiredHolderFieldIds = isPosChart
    ? [...DEFAULT_POSITION_PLAN_FIELDS.holder]
    : kDefaultPlanFields;
  const requiredPositionFieldIds = isPosChart ? [...DEFAULT_POSITION_PLAN_FIELDS.position] : [];

  // Required fields
  const requiredHolderFields = GetFieldNamesFromId(
    requiredHolderFieldIds,
    enumObjClassNames.box,
    mockDoc
  );
  const requiredPositionFields = isPosChart
    ? GetFieldNamesFromId(requiredPositionFieldIds, enumObjClassNames.position, mockDoc)
    : [];

  // Additional fields
  const additionalHolderFields = GetPlanAdditionalFieldOptions(
    [],
    requiredHolderFields,
    enumObjClassNames.box,
    mockDoc
  );
  const additionalPositionFields = GetPlanAdditionalFieldOptions(
    [],
    requiredPositionFields,
    enumObjClassNames.position,
    mockDoc
  );

  // Localization maps
  const holderPlanLocalizationMap = GetFieldNameLocalizationMap(
    GetPlanFieldsLocalizationMap(enumObjClassNames.box, isPosChart) ?? {},
    enumObjClassNames.box,
    mockDoc
  );
  const positionPlanLocalizationMap = GetFieldNameLocalizationMap(
    GetPlanFieldsLocalizationMap(enumObjClassNames.position, isPosChart) ?? {},
    enumObjClassNames.position,
    mockDoc
  );

  const holderFields = filterInternalFields(
    Array.from(new Set([...requiredHolderFields, ...additionalHolderFields]))
  )
    .sort((a, b) => a.localeCompare(b))
    .map(fieldName => ({
      fName:
        holderPlanLocalizationMap[fieldName] ??
        GetFieldLabelFromName(fieldName, enumObjClassNames.box, mockDoc),
      fValue: fieldName,
      isDisabled: requiredHolderFields.includes(fieldName)
    }));

  const positionFields = isPosChart
    ? filterInternalFields(
        Array.from(new Set([...requiredPositionFields, ...additionalPositionFields]))
      )
        .sort((a, b) => a.localeCompare(b))
        .map(fieldName => ({
          fName:
            positionPlanLocalizationMap[fieldName] ??
            GetFieldLabelFromName(fieldName, enumObjClassNames.position, mockDoc),
          fValue: fieldName,
          isDisabled: requiredPositionFields.includes(fieldName)
        }))
    : [];

  // Search filter
  const applySearchFilter = (fields: { fName: string; fValue: string; isDisabled: boolean }[]) => {
    return fields.filter(field => field.fName.toLowerCase().includes(searchQuery.toLowerCase()));
  };

  const filteredFieldOptions = {
    holder: applySearchFilter(holderFields),
    position: applySearchFilter(positionFields)
  };

  return {
    requiredHolderFields,
    requiredPositionFields,
    holderFields,
    positionFields,
    filteredFieldOptions
  };
}
